import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { clientApi, setClientToken, getClientToken, CLIENT_API } from "../../lib/clientApi";
import { Search, Filter, Calendar, ArrowUpDown, FileText, Download, Eye, LogOut, FileIcon, FileImage, FileSpreadsheet, FileArchive, Edit3, Clock, CheckCircle2, Save, AlertCircle, ArrowRight } from "lucide-react";

const FILE_ICONS = {
  pdf: FileText, doc: FileText, docx: FileText,
  xls: FileSpreadsheet, xlsx: FileSpreadsheet,
  jpg: FileImage, jpeg: FileImage, png: FileImage,
  zip: FileArchive,
};
function pickIcon(name) {
  const ext = (name || "").split(".").pop().toLowerCase();
  return FILE_ICONS[ext] || FileIcon;
}
function humanSize(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function ClientDashboard() {
  const [me, setMe] = useState(null);
  const [docs, setDocs] = useState([]);
  const [cats, setCats] = useState([]);
  const [forms, setForms] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [year, setYear] = useState("");
  const [sort, setSort] = useState("uploaded_at_desc");
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    if (!getClientToken()) {
      nav("/area-riservata", { replace: true });
      return;
    }
    clientApi.get("/client/me")
      .then((r) => setMe(r.data))
      .catch(() => { setClientToken(null); nav("/area-riservata", { replace: true }); });
    clientApi.get("/client/document-categories").then((r) => setCats(r.data || [])).catch(() => {});
    clientApi.get("/client/fillable-forms").then((r) => setForms(r.data || [])).catch(() => {});
  }, [nav]);

  useEffect(() => {
    if (!me) return;
    setLoading(true);
    const params = { sort };
    if (q.trim()) params.q = q.trim();
    if (cat) params.category = cat;
    if (year) params.year = Number(year);
    clientApi.get("/client/documents", { params })
      .then((r) => setDocs(r.data || []))
      .finally(() => setLoading(false));
  }, [me, q, cat, year, sort]);

  const years = useMemo(() => {
    const set = new Set();
    docs.forEach((d) => { if (d.uploaded_at) set.add(d.uploaded_at.slice(0, 4)); });
    return Array.from(set).sort().reverse();
  }, [docs]);

  const handleDownload = async (doc) => {
    try {
      const token = getClientToken();
      const res = await fetch(`${CLIENT_API}/client/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download fallito");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert("Impossibile scaricare il documento. Riprova.");
    }
  };

  const handleView = async (doc) => {
    try {
      const token = getClientToken();
      const res = await fetch(`${CLIENT_API}/client/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      // do not revoke immediately so tab can read
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      alert("Impossibile aprire il documento.");
    }
  };

  const logout = () => {
    setClientToken(null);
    nav("/area-riservata", { replace: true });
  };

  if (!me) return <div className="h-[60vh] flex items-center justify-center text-[#6B7280]">Caricamento…</div>;

  return (
    <div data-testid="client-dashboard" className="min-h-[80vh] py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#007A3D]">Area Riservata</span>
            <h1 className="mt-3 font-display font-black tracking-tighter text-3xl sm:text-4xl text-[#1A1A1A]">
              Benvenuto, {me.name || me.username}
            </h1>
            <p className="mt-3 text-sm text-[#6B7280]">
              Qui trovi tutti i documenti che lo studio Betelgez ha caricato per te.
            </p>
          </div>
          <button
            data-testid="client-logout"
            onClick={logout}
            className="inline-flex items-center gap-2 text-sm font-medium text-[#1A1A1A] hover:text-[#B91C1C] border border-[#E5E7EB] hover:border-[#FECACA] rounded-full px-5 py-2.5 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>

        {/* Fillable Forms */}
        {forms.length > 0 && (
          <div className="mb-8" data-testid="client-forms-section">
            <div className="flex items-center gap-2 mb-3">
              <Edit3 className="w-4 h-4 text-[#007A3D]" />
              <h2 className="font-display font-bold text-lg text-[#1A1A1A]">Documenti da compilare</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {forms.map((f) => {
                const st = {
                  da_compilare: { label: "Da compilare", color: "bg-[#FEF3C7] text-[#B45309]", Icon: Clock },
                  bozza: { label: "Bozza salvata", color: "bg-[#DBEAFE] text-[#1E40AF]", Icon: Save },
                  compilato: { label: "Compilato", color: "bg-[#EAF8F0] text-[#007A3D]", Icon: CheckCircle2 },
                  approvato: { label: "Approvato", color: "bg-[#D1FAE5] text-[#065F46]", Icon: CheckCircle2 },
                  da_correggere: { label: "Da correggere", color: "bg-[#FEE2E2] text-[#B91C1C]", Icon: AlertCircle },
                }[f.status] || { label: "Da compilare", color: "bg-[#FEF3C7] text-[#B45309]", Icon: Clock };
                return (
                  <Link
                    key={f.id}
                    to={`/area-riservata/documento/${f.id}`}
                    data-testid={`form-card-${f.id}`}
                    className="group bg-white rounded-2xl border border-[#E5E7EB] hover:border-[#00A651] shadow-sm hover:shadow-md p-5 transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${st.color}`}>
                          <st.Icon className="w-3 h-3" /> {st.label}
                        </span>
                        <div className="mt-2 font-semibold text-[#1A1A1A] text-sm truncate">{f.title}</div>
                        {f.description && <div className="text-xs text-[#6B7280] mt-1 line-clamp-2">{f.description}</div>}
                        <div className="text-xs text-[#6B7280] mt-2">{f.fields?.length || 0} campi</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#007A3D] shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-3xl border border-[#E5E7EB] shadow-sm p-5 md:p-6 mb-6 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
          <div className="md:col-span-5 relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <input
              data-testid="docs-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca tra i documenti"
              className="w-full rounded-xl border border-[#E5E7EB] pl-11 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#00A651]"
            />
          </div>
          <div className="md:col-span-3 relative">
            <Filter className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <select
              data-testid="docs-cat"
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              className="w-full rounded-xl border border-[#E5E7EB] pl-11 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#00A651] appearance-none bg-white"
            >
              <option value="">Tutte le categorie</option>
              {cats.map((c) => <option key={c.slug} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div className="md:col-span-2 relative">
            <Calendar className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <select
              data-testid="docs-year"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="w-full rounded-xl border border-[#E5E7EB] pl-11 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#00A651] appearance-none bg-white"
            >
              <option value="">Tutti gli anni</option>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="md:col-span-2 relative">
            <ArrowUpDown className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <select
              data-testid="docs-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="w-full rounded-xl border border-[#E5E7EB] pl-11 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#00A651] appearance-none bg-white"
            >
              <option value="uploaded_at_desc">Più recenti</option>
              <option value="uploaded_at_asc">Più vecchi</option>
              <option value="title_asc">Titolo A-Z</option>
              <option value="title_desc">Titolo Z-A</option>
            </select>
          </div>
        </div>

        {/* Documents */}
        <div className="bg-white rounded-3xl border border-[#E5E7EB] shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-[#6B7280]">Caricamento documenti…</div>
          ) : docs.length === 0 ? (
            <div className="p-12 text-center text-[#6B7280]">
              <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
              Nessun documento {q || cat || year ? "che corrisponda ai filtri" : "ancora caricato dallo studio"}.
            </div>
          ) : (
            <ul className="divide-y divide-[#F3F4F6]">
              {docs.map((d) => {
                const Icon = pickIcon(d.filename);
                return (
                  <li key={d.id} data-testid={`doc-row-${d.id}`} className="px-5 md:px-7 py-4 md:py-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5 hover:bg-[#F9FAFB] transition-colors">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-11 h-11 rounded-xl bg-[#EAF8F0] text-[#007A3D] flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-[#1A1A1A] text-sm md:text-base truncate">{d.title || d.filename}</div>
                        <div className="text-xs text-[#6B7280] mt-0.5 truncate">
                          {d.category} · {humanSize(d.size)} · {fmtDate(d.uploaded_at)}
                        </div>
                        {d.description && (
                          <div className="text-xs text-[#6B7280] mt-1 line-clamp-1">{d.description}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        data-testid={`doc-view-${d.id}`}
                        onClick={() => handleView(d)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full border border-[#E5E7EB] hover:border-[#00A651] hover:text-[#007A3D] transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" /> Visualizza
                      </button>
                      <button
                        data-testid={`doc-download-${d.id}`}
                        onClick={() => handleDownload(d)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full bg-[#007A3D] hover:bg-[#00A651] text-white transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" /> Scarica
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
